import classNames from 'classnames'
import { FC, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'next-i18next'
import { useRafLoop } from 'react-use'
import styled from 'styled-components'

import { poppins } from '@/components/common/fonts'

export const ResultProgress: FC<{
  /**
   * @default 1000 * 60 // 1분
   * unit: ms
   */
  duration?: number
  isGenerating?: boolean
  className?: string
}> = ({ duration = 1000 * 60, isGenerating, className }) => {
  const { t } = useTranslation('common')

  const [startedAt, setStartedAt] = useState<number | null>(null)
  const [currentTime, setCurrentTime] = useState<number | null>(null)

  const progress = useMemo(() => {
    if (!isGenerating) {
      return 100
    }

    if (!startedAt) {
      return 0
    }

    if (!currentTime) {
      return 0
    }

    const elapsed = currentTime - startedAt

    if (elapsed > duration) {
      return 100
    }

    return (elapsed / duration) * 100
  }, [isGenerating, startedAt, currentTime, duration])

  const [stop, start] = useRafLoop(() => {
    setCurrentTime(Date.now())
  }, false)

  useEffect(() => {
    if (isGenerating) {
      setStartedAt(Date.now())
      start()
    } else {
      stop()
    }
  }, [isGenerating, start, stop])

  const progressText = `${Math.floor(progress)}%`

  return (
    <ResultProgressContainer className={className}>
      <ProgressDescription>
        <span className="title">{t('In Progress')}</span>
        <span className="progress">{progressText}</span>
      </ProgressDescription>

      <ProgressBar
        className={classNames({
          completed: !isGenerating,
        })}
      >
        <ProgressBarInner
          style={{
            width: `${progress}%`,
            transition: isGenerating ? undefined : 'width 200ms',
          }}
        />
      </ProgressBar>
    </ResultProgressContainer>
  )
}

const ResultProgressContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

/**
 * - .title: 제목
 * - .progress: 퍼센티지
 */
const ProgressDescription = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  font-family: ${poppins.string};

  .title {
    font-size: 16px;
    line-height: 12px;
    font-weight: 500;
    color: #000000;
  }

  .progress {
    font-size: 18px;
    line-height: 16px;
    font-weight: 600;
    color: #3075f6;
  }
`

const ProgressBar = styled.div`
  position: relative;

  width: 100%;
  height: 8px;

  border-radius: 9999px;
  background-color: #f1f2f480;
`

const ProgressBarInner = styled.div`
  position: absolute;
  inset: 0;

  background-image: linear-gradient(
    89.98deg,
    #17ead9 1.15%,
    #6078ea 67.72%,
    #3075f6 128.52%
  );

  border-radius: 9999px;
  min-width: 8px;
`
